import "./tsh-hero";
import "./tsh-platform-bar";
import "./tsh-session";
import "./tsh-prose";
import "./tsh-features";
import "./tsh-cta";

import type { LonaWebComponent } from "@tento-ui/component";
import type { Brand } from "@tento-ui/brand/brand";
import type { NodeRenderer, SectionSet } from "@tento-ui/brand/page-engine.types";
import type { PageNode } from "@tento-ui/brand/page-model";

import type { PageEngineNodeProps } from "../to-props";

import { TshCta } from "./tsh-cta";
import { TshFeatures } from "./tsh-features";
import { TshHero } from "./tsh-hero";
import { TshProse } from "./tsh-prose";
import { TshSession } from "./tsh-session";

// tsh's own section set.
//
// It reuses the shipped node KINDS rather than inventing any — the same closed
// union the Rust skeleton emits — so this page is crawlable without a wire
// change. The mapping is where the design lives:
//
//   hero          -> the sentence, the command, the three facts
//   media         -> a captured session, replayed  <- the showcase figure
//   prose         -> running explanation on a 560px measure
//   feature-grid  -> a LIST, not a grid of cards
//   cta           -> the command again, and one link
//
// `media` doing the heavy lifting is the point of the redesign: the page this
// replaces has four card grids and no terminal, which is a page about a shell
// that shows no shell.

type SectionComponent = LonaWebComponent & { bind(node: PageNode): void };

function tshRenderer(factory: () => SectionComponent): NodeRenderer<PageEngineNodeProps> {
  // Both entry points build fresh rather than re-binding in place. These
  // sections are static once bound and the engine calls `bind` on navigation
  // with a different node entirely, so reusing the element buys nothing and
  // costs the class of bug where yesterday's content survives in a slot the
  // new node did not fill.
  const render = (props: PageEngineNodeProps): HTMLElement => {
    const $el = factory();
    $el.bind(props.node);
    return $el;
  };
  return { make: render, bind: (_$el, props) => render(props), skeleton: null };
}

// No header, no footer, no nav — only the sections, stacked.
//
// The Lona workspace nav names Calendar, Stickers and Sheets, which have
// nothing to do with installing a shell; the page being replaced already
// refused it and said so in a comment, and this keeps that decision. The
// layout therefore drops all three parts it is handed rather than arranging
// them.
const TSH_CHROME: Brand.ChromeVisitor = {
  makeLayout: () => {
    const $layout = document.createElement("div");
    $layout.id = "tsh-layout";
    return $layout;
  },
};

export const tshSectionSet: SectionSet<PageEngineNodeProps> = {
  chrome: TSH_CHROME,
  sections: {
    hero: tshRenderer(() => TshHero.make()),
    media: tshRenderer(() => TshSession.make()),
    prose: tshRenderer(() => TshProse.make()),
    "feature-grid": tshRenderer(() => TshFeatures.make()),
    cta: tshRenderer(() => TshCta.make()),
  },
};
